import type { AppError, AsyncResult } from '@shared/utils';
import { err, ok, tryCatchAsync } from '@shared/utils';
import { RuntimeBoundFacade, BaseRuntimeManager } from '@shared/core';
import type { PackageResourceConfig } from '@shared/core';
import type { LoggerEnvironmentConfig } from '../config/interfaces/LoggerEnvironmentConfig';
import { LOG_LEVELS } from '../constants/LOG_LEVELS';
import { LEVEL_PRIORITY } from '../constants/LEVEL_PRIORITY';
import type { LogLevel } from '../types/LogLevel';
import type { LogEntry } from '../types/LogEntry';
import { LOGGER_ERROR_CODES } from '../constants/LOGGER_ERROR_CODES';
import { LOGGER_MESSAGES } from '../constants/LOGGER_MESSAGES';

class LoggerInstance {
  private readonly runtimeManager = new BaseRuntimeManager<LoggerEnvironmentConfig>();

  setConfig(config: PackageResourceConfig<LoggerEnvironmentConfig>): void {
    this.runtimeManager.setConfig(config);
  }

  async log(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: Error,
  ): AsyncResult<boolean, AppError> {
    const planResult = this.runtimeManager.resolveExecutionPlan();
    if (planResult.isErr) {
      return err(planResult.error);
    }

    const { envConfig } = planResult.value;

    if (LEVEL_PRIORITY[level] < LEVEL_PRIORITY[envConfig.options.level]) {
      return ok(false);
    }

    if (envConfig.adapters.length === 0) {
      return err({
        errorCode: LOGGER_ERROR_CODES.NO_ADAPTERS,
        errorMessage: LOGGER_MESSAGES.NO_ADAPTERS,
      });
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      error,
    };

    const adapterResult = await tryCatchAsync(async () => {
      await Promise.all(envConfig.adapters.map((adapter) => adapter.execute(entry)));
      return true;
    });

    return adapterResult.match({
      ok: (success) => ok(success),
      err: (e) => err({
        errorCode: LOGGER_ERROR_CODES.ADAPTER_ERROR,
        errorMessage: `${LOGGER_MESSAGES.ADAPTER_ERROR}: ${e instanceof Error ? e.message : String(e)}`,
      }),
    });
  }
}

export class Logger extends RuntimeBoundFacade<LoggerInstance> {
  protected readonly facadeKey = 'logger';

  private static facade = new Logger();

  private constructor() {
    super();
  }

  static configureRuntime(config: PackageResourceConfig<LoggerEnvironmentConfig>): void {
    const instance = new LoggerInstance();
    instance.setConfig(config);
    Logger.facade.setInstance(instance);
  }

  handle(request: LogEntry): AsyncResult<boolean, AppError> {
    return this.callWithInstanceResult((instance) =>
      instance.log(request.level, request.message, request.context, request.error)
    );
  }

  static async debug(
    message: string,
    context?: Record<string, unknown>,
  ): AsyncResult<boolean, AppError> {
    return Logger.facade.callWithInstanceResult((instance) =>
      instance.log(LOG_LEVELS.DEBUG, message, context)
    );
  }

  static async info(
    message: string,
    context?: Record<string, unknown>,
  ): AsyncResult<boolean, AppError> {
    return Logger.facade.callWithInstanceResult((instance) =>
      instance.log(LOG_LEVELS.INFO, message, context)
    );
  }

  static async warn(
    message: string,
    context?: Record<string, unknown>,
  ): AsyncResult<boolean, AppError> {
    return Logger.facade.callWithInstanceResult((instance) =>
      instance.log(LOG_LEVELS.WARN, message, context)
    );
  }

  static async error(
    message: string,
    context?: Record<string, unknown>,
    error?: Error,
  ): AsyncResult<boolean, AppError> {
    return Logger.facade.callWithInstanceResult((instance) =>
      instance.log(LOG_LEVELS.ERROR, message, context, error)
    );
  }

  static resetInstance(): void {
    Logger.facade.resetInstance();
    Logger.facade = new Logger();
  }

  static clearRuntime(): void {
    Logger.resetInstance();
  }
}
